import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Input from '@cloudscape-design/components/input';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Select from '@cloudscape-design/components/select';
import Pagination from '@cloudscape-design/components/pagination';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Link from '@cloudscape-design/components/link';
import { getEvents, listStreams } from '../../api/events';
import { resolveEventType } from '../../utils/resolveEventType';
import { isBedrockEvent } from '../../utils/isBedrockEvent';
import { AiBadge } from '../../components/AiBadge';
import { startReplay } from '../../api/replay';
import { getCredentials } from '../../api/client';
import { EventDetailPanel } from '../../components/EventDetailPanel';
import { EventTypeIndicator, getEventTypeColor } from '../../components/EventTypeIndicator';
import { CausedByChip } from '../../components/CausedByChip';
import { WriteEventForm } from '../../components/WriteEventForm';
import { CausalityDag } from '../../components/CausalityDag';
import { StreamKeyInput, buildKeyPayload, isKeyComplete, buildStreamDisplay } from '../../components/StreamKeyInput';
import { formatTimestamp, getStoredTimeFormat, setStoredTimeFormat, TimeFormat } from '../../utils/timeFormat';

interface StreamKey {
  fields?: string[];
  delimiter?: string;
}

interface EventsTabProps {
  storeName: string;
  isActive: boolean;
  streamKey?: StreamKey;
}

interface EventItem {
  metadata?: {
    eventId?: string;
    eventType?: string;
    version?: number;
    sequence?: number;
    timestamp?: string;
    source?: string;
    causedBy?: string | string[] | null;
    [key: string]: unknown;
  };
  version?: number;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

// resolveEventType lives in ../../utils/resolveEventType — imported above.
// EventItem's metadata shape is compatible with the helper's parameter type.

function getPreview(data: unknown): string {
  if (!data || typeof data !== 'object') return '—';
  const priorityFields = ['status', 'amount', 'totalAmount', 'score', 'riskLevel', 'result', 'message', 'action'];
  const entries = Object.entries(data as Record<string, unknown>);
  const priority = entries.filter(([k]) => priorityFields.includes(k));
  const rest = entries.filter(([k]) => !priorityFields.includes(k));
  const selected = [...priority, ...rest].slice(0, 3);
  return selected.map(([, v]) => {
    const val = typeof v === 'object' ? JSON.stringify(v).substring(0, 20) : String(v);
    return val;
  }).join(' · ') || '—';
}

function relativeTime(ts?: string): string {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

// --- Tree View ---
function TreeView({ events, onSelect, newEventIds }: { events: EventItem[]; onSelect: (item: EventItem) => void; newEventIds: Set<string> }) {
  // Group by causedBy to build parent-child relationships
  const eventMap = new Map<string, EventItem>();
  events.forEach(e => { if (e.metadata?.eventId) eventMap.set(e.metadata.eventId, e); });

  // Find roots (events with no causedBy or causedBy not in current set)
  const childrenMap = new Map<string, EventItem[]>();
  const roots: EventItem[] = [];

  events.forEach(e => {
    const causedBy = e.metadata?.causedBy;
    const parentIds = causedBy ? (Array.isArray(causedBy) ? causedBy : [causedBy]) : [];
    let attached = false;
    for (const pid of parentIds) {
      if (eventMap.has(pid)) {
        const existing = childrenMap.get(pid) || [];
        existing.push(e);
        childrenMap.set(pid, existing);
        attached = true;
        break;
      }
    }
    if (!attached) roots.push(e);
  });

  function renderNode(item: EventItem, depth: number, fallbackKey: string): React.ReactNode {
    const id = item.metadata?.eventId || '';
    const children = childrenMap.get(id) || [];
    return (
      <div key={id || fallbackKey} style={{ marginLeft: depth * 20 }}>
        <div
          onClick={() => onSelect(item)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 8px',
            cursor: 'pointer',
            borderLeft: depth > 0 ? '2px solid #d1d5db' : 'none',
            marginLeft: depth > 0 ? '-2px' : 0,
            borderRadius: '4px',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f4f4f4')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <EventTypeIndicator eventType={resolveEventType(item)} />
          {isBedrockEvent(item) && <AiBadge />}
          {newEventIds.has(item.metadata?.eventId || '') && (
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#fff', background: '#4caf50', padding: '1px 4px', borderRadius: 3 }}>NEW</span>
          )}
          <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#687078' }}>
            v{item.metadata?.version ?? item.version ?? '?'}
          </span>
          <span style={{ fontSize: '12px', color: '#9ba7b3' }}>
            {relativeTime(item.metadata?.timestamp)}
          </span>
        </div>
        {children.map((child, i) => renderNode(child, depth + 1, `${fallbackKey}-${i}`))}
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      {roots.length === 0 && <Box color="text-body-secondary" padding="l">No events to display</Box>}
      {roots.map((r, i) => renderNode(r, 0, `root-${i}`))}
    </div>
  );
}

// --- Compact View ---
function CompactView({ events, onSelect }: { events: EventItem[]; onSelect: (item: EventItem) => void }) {
  if (events.length === 0) return <Box color="text-body-secondary" padding="l">No events to display</Box>;
  return (
    <div style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.8' }}>
      {events.map((item, idx) => {
        const id = item.metadata?.eventId || `event-${idx}`;
        const eventType = resolveEventType(item);
        const version = item.metadata?.version ?? item.version ?? '?';
        const stream = (item as any).stream || '—';
        const time = relativeTime(item.metadata?.timestamp);
        const dotColor = getEventTypeColor(eventType);
        return (
          <div
            key={id}
            onClick={() => onSelect(item)}
            style={{ padding: '2px 8px', cursor: 'pointer', borderRadius: '3px', display: 'flex', alignItems: 'center', gap: '8px' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f4f4f4')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dotColor, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontWeight: 600, minWidth: '140px' }}>{eventType}</span>
            {isBedrockEvent(item) && <AiBadge />}
            <span style={{ color: '#687078' }}>v{version}</span>
            <span style={{ color: '#9ba7b3' }}>·</span>
            <span style={{ color: '#687078' }}>{stream}</span>
            <span style={{ color: '#9ba7b3' }}>·</span>
            <span style={{ color: '#9ba7b3' }}>{time}</span>
          </div>
        );
      })}
    </div>
  );
}

export function EventsTab({ storeName, isActive, streamKey }: EventsTabProps) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<string>('table');
  const [stream, setStream] = useState('');
  const [keyFields, setKeyFields] = useState<Record<string, string>>({});
  const [submittedStream, setSubmittedStream] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [filterType, setFilterType] = useState('');
  const [submittedFilter, setSubmittedFilter] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);
  const [liveTail, setLiveTail] = useState(true);
  const [browseAll, setBrowseAll] = useState(true);
  const seenEventIds = useRef<Set<string>>(new Set());
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(getStoredTimeFormat);
  const [replayModalVisible, setReplayModalVisible] = useState(false);
  const [replayStart, setReplayStart] = useState('');
  const [replayEnd, setReplayEnd] = useState('');
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState('');
  const queryClient = useQueryClient();

  const { data: streamsData, refetch: refetchStreams } = useQuery({
    queryKey: ['streams', storeName],
    queryFn: () => listStreams(storeName),
    enabled: !!getCredentials(),
  });
  const streamOptions = (streamsData?.streams || []).map(s => ({ value: s }));

  useEffect(() => {
    setStoredTimeFormat(timeFormat);
  }, [timeFormat]);

  const currentToken = tokenHistory[currentPageIndex - 1];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['events', storeName, submittedStream, currentToken, submittedFilter, browseAll],
    queryFn: () => getEvents(storeName, submittedStream, {
      limit: 25,
      // Browse-all wants the freshest activity first; a specific stream is
      // usually walked chronologically (asc = oldest first).
      sort: submittedStream ? 'asc' : 'desc',
      filter: submittedFilter ? `metadata.eventType=${submittedFilter}` : undefined,
      cursor: currentToken,
    }),
    enabled: (!!submittedStream || browseAll) && !!getCredentials(),
    refetchInterval: liveTail ? 2000 : false,
  });

  const responseData = data?.data as { events?: EventItem[]; items?: EventItem[]; cursor?: string; hasMore?: boolean } | null;
  const events: EventItem[] = responseData?.events || responseData?.items || [];
  const nextCursor = responseData?.cursor;

  // Track new events for delta highlighting
  const newEventIds = new Set<string>();
  events.forEach((e) => {
    const id = e.metadata?.eventId;
    if (id && !seenEventIds.current.has(id)) {
      if (seenEventIds.current.size > 0) newEventIds.add(id); // Don't highlight initial load
      seenEventIds.current.add(id);
    }
  });

  const handleLoad = () => {
    if (streamKey?.fields && streamKey.fields.length > 1) {
      if (!isKeyComplete(streamKey, stream, keyFields)) return;
    }
    const payload = buildKeyPayload(streamKey, stream, keyFields);
    const streamValue = payload.stream || buildStreamDisplay(streamKey, keyFields);
    setSubmittedStream(streamValue);
    setBrowseAll(!streamValue);
    setSubmittedFilter(filterType);
    setSelectedEvent(null);
    setCurrentPageIndex(1);
    setTokenHistory([undefined]);
  };

  // Load a specific stream from a click in the events table. Skips the
  // stream-key-composition path since streamValue is already fully qualified.
  const loadStream = (streamValue: string) => {
    setStream(streamValue);
    setSubmittedStream(streamValue);
    setBrowseAll(false);
    setSubmittedFilter(filterType);
    setSelectedEvent(null);
    setCurrentPageIndex(1);
    setTokenHistory([undefined]);
  };

  const handleKeyDown = (e: CustomEvent<{ key: string }>) => {
    if (e.detail.key === 'Enter') {
      handleLoad();
    }
  };

  const handleNextPage = () => {
    if (nextCursor) {
      const newIndex = currentPageIndex + 1;
      if (tokenHistory.length <= newIndex - 1) {
        setTokenHistory([...tokenHistory, nextCursor]);
      }
      setCurrentPageIndex(newIndex);
    }
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 1) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleRefresh = () => {
    refetch();
    refetchStreams();
  };

  const handleSelectEvent = (item: EventItem) => {
    setSelectedEvent(item);
  };

  const timeFormatOptions = [
    { value: 'utc', label: 'UTC' },
    { value: 'local', label: 'Local' },
    { value: 'relative', label: 'Relative' },
  ];

  const handleReplay = async () => {
    setReplayLoading(true);
    setReplayError('');
    try {
      await startReplay(storeName, replayStart, replayEnd);
      setReplayModalVisible(false);
      setReplayStart('');
      setReplayEnd('');
    } catch (err: unknown) {
      setReplayError((err as Error).message || 'Replay failed');
    } finally {
      setReplayLoading(false);
    }
  };

  return (
    <SpaceBetween size="l">
      {/* Toolbar */}
      <SpaceBetween size="s" direction="horizontal">
        <StreamKeyInput
          streamKey={streamKey}
          stream={stream}
          onStreamChange={setStream}
          keyFields={keyFields}
          onKeyFieldsChange={setKeyFields}
          onKeyDown={handleKeyDown}
          streamOptions={streamOptions}
        />
        <div style={{ minWidth: '200px' }}>
          <Input
            value={filterType}
            onChange={({ detail }) => setFilterType(detail.value)}
            onKeyDown={handleKeyDown}
            placeholder="Filter by event type..."
          />
        </div>
        <Button onClick={handleLoad}>Load</Button>
        <Button iconName="refresh" variant="icon" onClick={handleRefresh} ariaLabel="Refresh" />
        <Button
          variant={liveTail ? 'primary' : 'normal'}
          iconName={liveTail ? 'status-positive' : 'status-stopped'}
          onClick={() => setLiveTail(!liveTail)}
        >
          {liveTail ? '⏸ Live' : '▶ Paused'}
        </Button>
        <Select
          selectedOption={timeFormatOptions.find(o => o.value === timeFormat)!}
          onChange={({ detail }) => setTimeFormat(detail.selectedOption.value as TimeFormat)}
          options={timeFormatOptions}
        />
        <Button onClick={() => setReplayModalVisible(true)}>Replay events</Button>
      </SpaceBetween>

      {/* View mode toggle */}
      <SegmentedControl
        selectedId={viewMode}
        onChange={({ detail }) => setViewMode(detail.selectedId)}
        options={[
          { id: 'table', text: 'Table' },
          { id: 'tree', text: 'Tree' },
          { id: 'dag', text: 'DAG' },
          { id: 'compact', text: 'Compact' },
        ]}
      />

      {/* Replay modal */}
      <Modal
        visible={replayModalVisible}
        onDismiss={() => setReplayModalVisible(false)}
        header="Replay events"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={() => setReplayModalVisible(false)}>Cancel</Button>
              <Button variant="primary" loading={replayLoading} onClick={handleReplay} disabled={!replayStart || !replayEnd}>Start replay</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {replayError && <Alert type="error">{replayError}</Alert>}
          {isError && <Alert type="error">Failed to load events. Check your credentials and try again.</Alert>}
          <FormField label="Start time (ISO 8601)">
            <Input value={replayStart} onChange={({ detail }) => setReplayStart(detail.value)} placeholder="2024-01-01T00:00:00Z" />
          </FormField>
          <FormField label="End time (ISO 8601)">
            <Input value={replayEnd} onChange={({ detail }) => setReplayEnd(detail.value)} placeholder="2024-01-02T00:00:00Z" />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Write event form (only when store is active) */}
      {isActive && (
        <WriteEventForm
          key={submittedStream}
          storeName={storeName}
          defaultPartitionKey={submittedStream}
          streamKey={streamKey}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['events', storeName] });
          }}
        />
      )}

      {/* Split layout: content + detail panel */}
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: selectedEvent ? '1 1 60%' : '1 1 100%', minWidth: 0 }}>
          {viewMode === 'table' && (
            <div style={{ overflowX: 'auto' }}>
              <Table
                loading={isLoading}
                loadingText="Loading events"
                items={events}
                onRowClick={({ detail }) => handleSelectEvent(detail.item)}
                header={
                  <Header counter={(submittedStream || browseAll) ? `(${events.length})` : undefined}>
                    Events
                  </Header>
                }
                columnDefinitions={[
                  ...(browseAll ? [{
                    id: 'stream',
                    header: 'Stream',
                    cell: (item: EventItem) => {
                      const s = (item as any).stream;
                      if (!s) return '—';
                      return (
                        <Link
                          fontSize="body-s"
                          onFollow={(e) => { e.preventDefault(); loadStream(s); }}
                        >
                          <span
                            style={{
                              fontFamily: 'monospace', fontSize: '12px',
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 130,
                              verticalAlign: 'middle',
                            }}
                            title={s}
                          >
                            {s}
                          </span>
                        </Link>
                      );
                    },
                    width: 150,
                  }] : []),
                  {
                    id: 'version',
                    header: 'Version',
                    cell: (item: EventItem) => (
                      <span style={{ fontFamily: 'monospace' }}>
                        {item.metadata?.version ?? item.version ?? item.metadata?.sequence ?? '—'}
                      </span>
                    ),
                    width: 70,
                  },
                  {
                    id: 'eventType',
                    header: 'Event type',
                    cell: (item: EventItem) => {
                      const et = resolveEventType(item);
                      const ai = isBedrockEvent(item);
                      if (et === '—') return <EventTypeIndicator eventType={et} />;
                      const href = `/stores/${storeName}/schemas/${encodeURIComponent(et)}`;
                      return (
                        <>
                          <Link
                            href={href}
                            fontSize="body-s"
                            onFollow={(e) => {
                              e.preventDefault();
                              navigate(href);
                            }}
                          >
                            <EventTypeIndicator eventType={et} />
                          </Link>
                          {ai && <AiBadge />}
                        </>
                      );
                    },
                    width: 200,
                  },
                  {
                    id: 'preview',
                    header: 'Preview',
                    cell: (item: EventItem) => {
                      const text = getPreview(item.data);
                      return (
                        <span
                          style={{
                            color: '#687078', fontSize: '13px',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%',
                          }}
                          title={text}
                        >
                          {text}
                        </span>
                      );
                    },
                    // Cap Preview so it doesn't crowd out Caused-by / Time.
                    // Cloudscape ignores this on browsers narrower than the table,
                    // but at typical viewports it prevents the collapse.
                    width: 260,
                    minWidth: 120,
                  },
                  {
                    id: 'causedBy',
                    header: 'Caused by',
                    cell: (item: EventItem) => (
                      <CausedByChip
                        causedBy={item.metadata?.causedBy}
                        storeName={storeName}
                        onSelect={(eventId) => {
                          const found = events.find((e) => e.metadata?.eventId === eventId);
                          if (found) {
                            handleSelectEvent(found);
                          } else {
                            // Cross-store causedBy — jump to lineage graph
                            navigate(`/stores/${storeName}/lineage/${eventId}`);
                          }
                        }}
                      />
                    ),
                    width: 130,
                  },
                  {
                    id: 'time',
                    header: 'Time',
                    cell: (item: EventItem) => formatTimestamp(item.metadata?.timestamp, timeFormat),
                    width: 130,
                  },
                  {
                    id: 'trace',
                    header: 'Trace',
                    cell: (item: EventItem) => item.metadata?.eventId ? (
                      <Button
                        variant="inline-link"
                        onClick={(e) => { e.stopPropagation(); navigate(`/stores/${storeName}/lineage/${item.metadata!.eventId}`); }}
                      >
                        Trace
                      </Button>
                    ) : '—',
                    width: 80,
                  },
                ]}
                empty={
                  <Box textAlign="center" padding="l">
                    <SpaceBetween size="m">
                      <b>{(submittedStream || browseAll) ? 'No events found' : 'Enter a stream ID to load events'}</b>
                      <Box variant="p" color="text-body-secondary">
                        {(submittedStream || browseAll)
                          ? 'No events in this stream yet. Write your first event.'
                          : 'Type a stream ID above and click Load to view events, or click Load with an empty stream to browse all.'}
                      </Box>
                    </SpaceBetween>
                  </Box>
                }
              />
              {(submittedStream || browseAll) && (
                <Box padding={{ top: 's' }}>
                  <Pagination
                    currentPageIndex={currentPageIndex}
                    pagesCount={nextCursor ? currentPageIndex + 1 : currentPageIndex}
                    onNextPageClick={handleNextPage}
                    onPreviousPageClick={handlePreviousPage}
                    openEnd={!!nextCursor}
                  />
                </Box>
              )}
            </div>
          )}

          {viewMode === 'tree' && (
            <TreeView events={events} onSelect={handleSelectEvent} newEventIds={newEventIds} />
          )}

          {viewMode === 'dag' && (
            <CausalityDag events={events} storeName={storeName} height={450} />
          )}

          {viewMode === 'compact' && (
            <CompactView events={events} onSelect={handleSelectEvent} />
          )}
        </div>

        {/* Detail panel */}
        {selectedEvent && (
          <Container
            header={
              <Header actions={<Button iconName="close" variant="icon" onClick={() => setSelectedEvent(null)} ariaLabel="Close" />}>
                Event detail
              </Header>
            }
          >
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              <EventDetailPanel
                event={selectedEvent as Record<string, unknown>}
                storeName={storeName}
                onClose={() => setSelectedEvent(null)}
                onSelectCausedBy={(eventId) => {
                  const found = events.find((e) => e.metadata?.eventId === eventId);
                  if (found) handleSelectEvent(found);
                }}
              />
            </div>
          </Container>
        )}
      </div>

      {/* Current State - aggregate view */}
      {events.length > 0 && (
        <Container header={<Header description="Computed by folding all events in this stream (latest values win)">Current State</Header>}>
          <pre style={{ margin: 0, fontSize: 12, overflow: 'auto', maxHeight: 400, whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(
              events.reduce<Record<string, unknown>>((acc, event) => {
                if (event.data && typeof event.data === 'object') {
                  return { ...acc, ...event.data };
                }
                return acc;
              }, {}),
              null,
              2
            )}
          </pre>
        </Container>
      )}
    </SpaceBetween>
  );
}
